import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import EmailIcon from '@mui/icons-material/Email';
import { analyticsApi } from '../api/analyticsApi';

import SendMessageModal from './SendMessageModal';

const formatEuropeanDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return [
        String(date.getDate()).padStart(2, '0'),
        String(date.getMonth() + 1).padStart(2, '0'),
        date.getFullYear()
    ].join('.');
};

const NewMembersRetention = ({ affiliateId, affiliateEmail, period }) => {
    const [newMembers, setNewMembers] = useState([]);
    const [guestRegistrations, setGuestRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);

    const [periodLabel, setPeriodLabel] = useState('');
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageOptionsOpen, setMessageOptionsOpen] = useState(false);
    const [messageTarget, setMessageTarget] = useState('members');
    const inactiveMembers = newMembers.filter((member) => !member.hasActivePlan);

    useEffect(() => {
        loadNewMembers();
    }, [period, affiliateId]);

    const loadNewMembers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const result = await analyticsApi.getNewMembers(token, affiliateId, period);
            setNewMembers(result.data);
            setGuestRegistrations(result.guestRegistrations || []);
            setPeriodLabel(result.periodLabel);
        } catch (error) {
            console.error('Error loading new members:', error);
        } finally {
            setLoading(false);
        }
    };



    const handleOpenMessageOptions = () => {
        setMessageOptionsOpen(true);
    };

    const handleSendMessageClick = (target) => {
        setMessageOptionsOpen(false);
        setMessageTarget(target);
        setMessageModalOpen(true);
    };

    return (
        <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" component="h2">
                    New Members Retention ({periodLabel})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<EmailIcon />}
                    onClick={handleOpenMessageOptions}
                    disabled={newMembers.length === 0 && guestRegistrations.length === 0}
                >
                    Message
                </Button>
            </Box>



            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Members
                    </Typography>
                    {newMembers.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            No new members during this period.
                        </Typography>
                    ) : (
                        <TableContainer sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Member Name</TableCell>
                                        <TableCell>Joined Date</TableCell>
                                        <TableCell align="center">Active Plan</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {newMembers.map((member) => (
                                        <TableRow key={member.userId} hover>
                                            <TableCell>{member.fullName}</TableCell>
                                            <TableCell>
                                                {formatEuropeanDate(member.createdAt)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={member.hasActivePlan ? "Has active plan" : "No active plan"}>
                                                    <FiberManualRecordIcon
                                                        sx={{
                                                            color: member.hasActivePlan ? 'green' : 'red',
                                                            fontSize: 14
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Guest Registrations ({guestRegistrations.length})
                    </Typography>
                    {guestRegistrations.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No guest registrations during this period.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Registered At</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {guestRegistrations.map((guest) => (
                                        <TableRow key={guest.id} hover>
                                            <TableCell>{`${guest.firstName} ${guest.lastName}`}</TableCell>
                                            <TableCell>{guest.email}</TableCell>
                                            <TableCell>
                                                {formatEuropeanDate(guest.createdAt)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            {/* Send Message Modal */}
            <Dialog
                open={messageOptionsOpen}
                onClose={() => setMessageOptionsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Select recipients</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<EmailIcon />}
                            onClick={() => handleSendMessageClick('members')}
                            disabled={newMembers.length === 0}
                            sx={{
                                backgroundColor: '#1f1f1f',
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: '#000000'
                                }
                            }}
                        >
                            Send to Members
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<EmailIcon />}
                            onClick={() => handleSendMessageClick('inactiveMembers')}
                            disabled={inactiveMembers.length === 0}
                        >
                            Send to Unactive Members
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<EmailIcon />}
                            onClick={() => handleSendMessageClick('guests')}
                            disabled={guestRegistrations.length === 0}
                        >
                            Send to Guests
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            onClick={() => handleSendMessageClick('allSelected')}
                            disabled={newMembers.length === 0 && guestRegistrations.length === 0}
                        >
                            All Selected Users
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <SendMessageModal
                open={messageModalOpen}
                onClose={() => setMessageModalOpen(false)}
                affiliate={affiliateId}
                affiliateEmail={affiliateEmail}
                preSelectedUsers={(messageTarget === 'members'
                    ? newMembers
                    : messageTarget === 'inactiveMembers'
                        ? inactiveMembers
                        : messageTarget === 'allSelected'
                            ? newMembers
                            : []).map(member => ({
                    id: member.userId,
                    fullName: member.fullName,
                    email: member.email
                }))}
                preSelectedGuests={(messageTarget === 'guests' || messageTarget === 'allSelected')
                    ? guestRegistrations.map(guest => ({
                        id: guest.id,
                        firstName: guest.firstName,
                        lastName: guest.lastName,
                        email: guest.email
                    }))
                    : []}
            />
        </Paper>
    );
};

export default NewMembersRetention;